<template>
  <div
    class="input-wrap"
    :class="{'input-wrap--error': error}"
    @input="onInput"
  >
    <component :is="wrapLabel ? 'label' : 'div'">
      <component
        v-if="label"
        :is="wrapLabel ? 'span' : 'label'"
        :for="id"
        class="input-wrap__label"
      >
        {{label}}&nbsp;{{isRequired ? '*' : ''}}

        <div
      v-if="message"
      class="input-wrap__info"
      :class="{'input-wrap__info--error': error}"
    >
      {{message}}
    </div>
      </component>

      <slot
        :id="id"
        :error="error"
        :touch="touchField"
      />

    </component>

  </div>
</template>

<script>
  import validationMessages, {displayMessage} from '../../lib/validationMessages';
  import unique from '../../lib/unique';
  import utils from '../mixins/utils';

  export default {
    mixins: [
      utils.translate,
    ],
    inject: {
      formValidationContext: {
        from: 'formValidationContext',
        default: null,
      },
    },
    props: {
      label: {
        type: String,
        default: null,
      },
      validations: {
        type: Object,
        default: null,
      },
      // This can be used to override the the logic which determines if the field is required.
      required: {
        type: Boolean,
        default: null,
      },
      validationMessages: {
        type: Object,
        default: null,
      },
      wrapLabel: {
        type: Boolean,
        default: true,
      },
      info: {
        type: String,
        default: null,
      },
      /**
       * When true, do not treat Vuelidate $dirty as “show errors”.
       * Used by InputWrapDate so VueDatePicker model sync does not surface required
       * errors before blur/touch or submit (formValidationContext).
       */
      errorsIgnoreVuelidateDirty: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        id: this.wrapLabel ? null : unique('InputWrap'),
        messages: {
          ...validationMessages,
          ...this.validationMessages,
        },
        // Track touched state for fields that don't use Vuelidate's $model.
        // Vuelidate v2 doesn't track touched state automatically.
        touched: false,
        // Captured input value on blur — used for validation message formatting
        // when Vuelidate's $model is unavailable (helpers.forEach per-item proxies).
        lastKnownValue: null,
      };
    },
    computed: {
      isRequired() {
        // Check for required in multiple ways to support both Vuelidate v1 and v2
        let hasRequiredProperty = false;

        if (this.validations) {
          // Vuelidate v2 style: check for 'required' property directly on validation object
          hasRequiredProperty = this.validations.hasOwnProperty('required') || 
                                Object.keys(this.validations).some(key => key === 'required');
          // Vuelidate v1/v2 style: check $params
          if (!hasRequiredProperty && this.validations.$params) {
            hasRequiredProperty = this.validations.$params.hasOwnProperty('required');
          }
        }
        return this.required === null ? hasRequiredProperty : this.required;
      },
      error() {
        if (!this.validations) return false;
        // For fields using Vuelidate's $model, $error already includes $dirty check
        // For fields using direct v-model binding, use our touched tracking
        // Check if $dirty exists and is being tracked properly
        const afterSubmitAttempt =
          this.formValidationContext && this.formValidationContext.submitAttempted === true;
        const vuelidateDirty =
          !this.errorsIgnoreVuelidateDirty && this.validations.$dirty === true;
        const isDirty = vuelidateDirty || this.touched || afterSubmitAttempt;
        return isDirty && this.validations.$invalid;
      },
      validationMessage() {
        if (!this.error) {
          return null;
        }

        // Vuelidate v2: use $silentErrors instead of $errors.
        // $errors is only populated when $dirty is true, but we have our own
        // touched/dirty tracking, so $errors may be empty when our error computed
        // returns true. $silentErrors is always populated when $invalid is true.
        const errors = this.validations?.$silentErrors || this.validations?.$errors || [];
        if (Array.isArray(errors) && errors.length > 0) {
          const firstError = errors[0];
          if (firstError) {
            // Prefer our Finnish translation map over the validator's own $message
            // (built-in validators like 'required' have English $message by default)
            if (firstError.$validator && this.messages[firstError.$validator]) {
              return displayMessage(this.messages[firstError.$validator], this.validations, firstError, this.lastKnownValue);
            }
            // Fall back to $message from the validator (e.g. from helpers.withMessage)
            if (firstError.$message) {
              return firstError.$message;
            }
          }
        }

        // Fallback: check for failed validators directly on the validation object
        if (this.validations) {
          const failedValidations = Object
            .keys(this.validations)
            .filter(key => !key.startsWith('$') && this.validations[key] === false);
          
          if (failedValidations.length > 0) {
            // Prioritise "required" over other messages
            const messageKey = failedValidations.includes('required') ? 'required' : failedValidations[0];

            // Try to find matching error entry for params (check all error sources)
            const allErrors = this.validations?.$silentErrors
              || this.validations?.$errors || [];
            const errorEntry = Array.isArray(allErrors)
              ? allErrors.find(e => e.$validator === messageKey)
              : null;

            // Use our Finnish translation map, passing errorEntry for param extraction.
            if (this.messages[messageKey]) {
              return displayMessage(this.messages[messageKey], this.validations, errorEntry, this.lastKnownValue);
            }
            // Fall back to the error entry's own $message (may contain Finnish
            // from helpers.withMessage on custom "Or" validators)
            if (errorEntry?.$message) {
              return typeof errorEntry.$message === 'function'
                ? errorEntry.$message({ $model: this.validations?.$model })
                : errorEntry.$message;
            }
          }
        }

        return null;
      },
      message() {
        return this.validationMessage
          ? this.validationMessage
          : this.info;
      },
    },
    methods: {
      // Capture current input value on every keystroke (native input event
      // bubbles from the child input/textarea). Keeps validation messages
      // like "Kentän minimipituus on 5 merkkiä. (3)" up to date in real time.
      onInput(event) {
        if (event.target && 'value' in event.target) {
          this.lastKnownValue = event.target.value;
        }
      },
      // Mark field as touched - call this on blur for fields not using Vuelidate's $model
      touchField() {
        this.touched = true;
        // Capture current input value for validation message formatting
        // (helpers.forEach per-item proxies don't expose $model)
        const input = this.$el?.querySelector('input, select, textarea');
        if (input) {
          this.lastKnownValue = input.value;
        }
        // Also call Vuelidate's $touch() if available to mark field as dirty
        if (this.validations && typeof this.validations.$touch === 'function') {
          this.validations.$touch();
        }
      },
    },
  }
</script>

<style lang="scss">
  @import "~styles/definitions";

  .input-wrap {
  }

  // When we have multiple inputs we want to set height the way
  // how labels and inputs align no matter how long the labels are.
  .form__row {
    .input-wrap {
      display: flex;
      flex-flow: column;

      &__label {
        flex: 1;
      }

      >label {
        display: flex;
        flex-flow: column;
        height: 100%;
        width: 100%;

        .input-text {
          width: 100%;
        }
      }
    }
  }

  .input-wrap__label {
    @include font-label-16;
    display: block;
    margin-bottom: r(8);
    transition: color 0.2s;

    .input-wrap:focus-within & {
      color: $color-green-2025;
    }

    .input-wrap--error &,
    .input-wrap--error:focus-within & {
      color: $color-red;
    }
  }

  .input-wrap__info {
    @include font-body-12;
    margin-top: r(4);

    &--error {
      color: $color-red;
    }
  }
</style>
